import type { VoidFunctionComponent } from 'react';

import { SBOM_PORTAL_PRIVATE } from '../../../../../shared/featureFlags/flagNames';
import { useRouter } from '../../../../contexts/RouterContext';
import { window } from '../../../../globals';
import { FeatureFlagRequired } from '../../../generic';
import FallbackImage from '../../../generic/FallbackImage';
import Link from '../../../generic/Link';
import Loader from '../../../generic/Loader';
import Tabs from '../../../generic/Tabs';
import Header from '../../../shared/settings/SettingsPageHeader';
import OrganizationSettingsPermissionGate from '../../OrganizationSettingsPermissionGate';
import {
  shouldDisplayOrganizationPortalSettings,
  shouldDisplayOrganizationPortalTokenSettings,
} from '../../shared/permissions';

import AccessTokens from './AccessTokens';
import Configuration from './Configuration';
import { usePortalSettings } from './hooks';

import styles from './SettingsOrganizationPortal.module.less';

type TabId = 'Configuration' | 'Access Tokens';
const tabIds: TabId[] = shouldDisplayOrganizationPortalTokenSettings()
  ? ['Configuration', 'Access Tokens']
  : ['Configuration'];

type Props = {
  activeTab: TabId;
};

/**
 * SettingsOrganizationPortal displays the organization portal settings page.
 */
const SettingsOrganizationPortal: VoidFunctionComponent<Props> = ({ activeTab }) => {
  const { push } = useRouter();
  const organizationId = window.user?.organizationId as number;
  const {
    settings,
    onUpdateSettings,
    isSaveButtonDisabled,
    isLoading,
    fileUrl,
    setFile,
    unsetFile,
    handleImgError,
    onSave,
    portalUrl,
    errors,
  } = usePortalSettings(organizationId);

  if (isLoading.fetch) {
    return <Loader />;
  }

  if (!window.portalUrl) {
    return (
      <FallbackImage src="404-cat" alt="Unconfigured Feature" hasPadding>
        <div>This feature is not configured</div>
        <p>Please ask your FOSSA administrator to enable it.</p>
      </FallbackImage>
    );
  }

  return (
    <div className={styles.portalSettings}>
      <Header heading="Portal">
        <Header.Paragraph>
          <span className="block">FOSSA provides a public portal for you to publish your SBOM files.</span>
          {portalUrl && (
            <span className="block">
              It will be accessible at{' '}
              <Link to={portalUrl} openInNewTab>
                {portalUrl}
              </Link>
            </span>
          )}
        </Header.Paragraph>
        <Header.Errors errors={errors} />
        {activeTab === 'Configuration' && (
          <Header.SaveButton isDisabled={isSaveButtonDisabled} onClick={onSave} isLoading={isLoading.save} />
        )}
      </Header>
      <FeatureFlagRequired flagName={SBOM_PORTAL_PRIVATE}>
        <Tabs>
          {tabIds.map((tab) => (
            <Tabs.Tab
              key={tab}
              label={tab}
              isActive={tab === activeTab}
              onSelect={() => {
                if (tab === 'Configuration') {
                  push('/account/settings/organization/portal');
                } else {
                  push('/account/settings/organization/portal/tokens');
                }
              }}
            />
          ))}
        </Tabs>
      </FeatureFlagRequired>
      {activeTab === 'Configuration' && (
        <Configuration
          settings={settings}
          onUpdateSettings={onUpdateSettings}
          fileUrl={fileUrl}
          setFile={setFile}
          unsetFile={unsetFile}
          handleImgError={handleImgError}
        />
      )}
      {activeTab === 'Access Tokens' && <AccessTokens />}
    </div>
  );
};

/**
 * SettingsOrganizationPortalContainer displays the organization portal settings page if the user has permission to view it.
 */
export const SettingsOrganizationPortalConfiguration = () => (
  <OrganizationSettingsPermissionGate checkPermission={shouldDisplayOrganizationPortalSettings}>
    <SettingsOrganizationPortal activeTab="Configuration" />
  </OrganizationSettingsPermissionGate>
);

export const SettingsOrganizationPortalTokens = () => (
  <OrganizationSettingsPermissionGate checkPermission={shouldDisplayOrganizationPortalTokenSettings}>
    <FeatureFlagRequired flagName={SBOM_PORTAL_PRIVATE}>
      <SettingsOrganizationPortal activeTab="Access Tokens" />
    </FeatureFlagRequired>
  </OrganizationSettingsPermissionGate>
);
